import type { AxiosInstance, AxiosResponse } from 'axios';
import { govApiLog } from '@gov-nx/core/app';
import type {
	DokumentyDokumentDto,
	DokumentyDokumentSeznamDto,
	DokumentyStrankovaniDto,
	PodaniPodaniInstanceDto,
} from '../generated';
import type {
	DocumentDeleteParams,
	DocumentRestoreParams,
	DocumentUploadParams,
} from '../types/queryTypes';

export const documentsQuery =
	(axiosInstance: AxiosInstance) =>
	async <T extends DokumentyStrankovaniDto>({
		...params
	}: T): Promise<DokumentyDokumentSeznamDto> => {
		govApiLog('/api/v1/dokumenty');
		const { data } = await axiosInstance.get('/api/v1/dokumenty', { params });
		return data;
	};

export const downloadDocumentQuery =
	(axiosInstance: AxiosInstance) =>
	async ({
		documentId,
		souborId,
	}: {
		documentId: number;
		souborId: string;
	}): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(
			`/api/v1/dokumenty/${documentId}/soubory/${souborId}/stahnout`,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
				responseType: 'blob',
			}
		);

export const downloadDocumentImageQuery =
	(axiosInstance: AxiosInstance) =>
	async ({
		documentId,
		souborId,
	}: {
		documentId: number;
		souborId: string;
	}): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(
			`/api/v1/dokumenty/${documentId}/soubory/${souborId}/stahnout`,
			{
				headers: {
					'Content-Type': 'multipart/form-data',
				},
				responseType: 'blob',
			}
		);

export const lastDocumentsQuery =
	(axiosInstance: AxiosInstance) =>
	async <Params>(
		params: Params
	): Promise<AxiosResponse<PodaniPodaniInstanceDto>> =>
		axiosInstance.post('/api/v1/podaniinstance/posledni', params, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});

interface documentsListQueryParams {
	jeSoubor?: true;
	kategorie?: string;
	smazano?: boolean;
	razeni?: string;
	start?: number;
	pocet?: number;
}

export const documentsListQuery =
	(axiosInstance: AxiosInstance) =>
	async <Params extends documentsListQueryParams>(
		params?: Params
	): Promise<DokumentyDokumentSeznamDto['seznam']> => {
		const { data } = await axiosInstance.get('/api/v1/dokumenty', { params });

		return data?.seznam || [];
	};
export const documentQuery =
	(axiosInstance: AxiosInstance) =>
	async (documentId: number): Promise<DokumentyDokumentDto> => {
		const { data } = await axiosInstance.get(`/api/v1/dokumenty/${documentId}`);

		return data;
	};

export const documentsPreviewQuery =
	(axiosInstance: AxiosInstance) =>
	async (documentId: number, souborId: string): Promise<Blob> => {
		const { data } = await axiosInstance.get(
			`/api/v1/dokumenty/${documentId}/soubory/${souborId}/nahled`,
			{
				responseType: 'blob',
			}
		);
		return data;
	};

export const updateDocumentQuery =
	(axiosInstance: AxiosInstance) =>
	async <Body>(dokumentId: number, body: Body): Promise<void> => {
		const { data } = await axiosInstance.post(
			`/api/v1/dokumenty/${dokumentId}`,
			body
		);
		return data;
	};

export const updateDocumentImagesQuery =
	(axiosInstance: AxiosInstance) =>
	async <Body>(dokumentId: number, body: Body): Promise<void> => {
		const { data } = await axiosInstance.post(
			`/api/v1/dokumenty/${dokumentId}/soubory`,
			body
		);
		return data;
	};

export const deleteDocumentImageQuery =
	(axiosInstance: AxiosInstance) =>
	async ({
		dokumentId,
		souborId,
	}: {
		dokumentId: number;
		souborId: string;
	}): Promise<AxiosResponse> => {
		const { data } = await axiosInstance.delete(
			`/api/v1/dokumenty/${dokumentId}/soubory/${souborId}`
		);
		return data;
	};

export const createDocumentQuery =
	(axiosInstance: AxiosInstance) =>
	async <Body>(body: Body): Promise<void> => {
		const { data } = await axiosInstance.post('/api/v1/dokumenty/', body);
		return data;
	};

export const documentDeleteQuery =
	(axiosInstance: AxiosInstance) =>
	async ({
		id,
		permanent = false,
	}: DocumentDeleteParams): Promise<AxiosResponse> =>
		axiosInstance.delete(`/api/v1/dokumenty/${id}`, {
			params: {
				tvrde: permanent,
			},
		});

export const documentRestoreQuery =
	(axiosInstance: AxiosInstance) =>
	async ({ id }: DocumentRestoreParams): Promise<AxiosResponse> =>
		axiosInstance.put(`/api/v1/dokumenty/${id}/obnovit`);

export const documentUploadQuery =
	(axiosInstance: AxiosInstance) =>
	async (params: DocumentUploadParams): Promise<AxiosResponse> => {
		govApiLog('/api/v1/dokumenty');
		return axiosInstance.post('/api/v1/dokumenty', params);
	};
